// WCAG 1.3.2 (Meaningful Sequence): uiv's popup mixin defaults
// `appendTo: 'body'`, moving every popover/tooltip out of reading order.
// This re-hosts the shown popup as the trigger's next sibling and switches
// it from uiv's document-coordinate `absolute` to a viewport-coordinate
// `fixed` position kept in sync on scroll, so it still paints where uiv put
// it without living at the end of <body>.
//
// Declines to relocate when an ancestor establishes a CSS containing block
// for `position: fixed` (transform/filter/perspective/backdrop-filter always;
// will-change/contain/content-visibility only for specific values) — a fixed
// descendant of such an ancestor paints relative to it, not the viewport, so
// relocating there would move where the popup paints.

const SIMPLE_CONTAINING_BLOCK_PROPERTIES = [
  'transform',
  'filter',
  'perspective',
  'backdropFilter',
]

// Only these `will-change` values name a property that itself establishes a
// containing block — `will-change: opacity` does not.
const WILL_CHANGE_CONTAINING_BLOCK_VALUES = new Set([
  'transform',
  'filter',
  'perspective',
  'backdrop-filter',
])

// Only these `contain` values establish a containing block — `size`/`style`
// alone do not.
const CONTAIN_CONTAINING_BLOCK_VALUES = new Set(['layout', 'paint', 'strict', 'content'])

function establishesContainingBlock(computedStyle) {
  if (
    SIMPLE_CONTAINING_BLOCK_PROPERTIES.some((prop) => {
      const value = computedStyle[prop]
      return value && value !== 'none'
    })
  ) {
    return true
  }

  const willChange = computedStyle.willChange
  if (
    willChange &&
    willChange !== 'none' &&
    willChange !== 'auto' &&
    willChange.split(',').some((prop) => WILL_CHANGE_CONTAINING_BLOCK_VALUES.has(prop.trim()))
  ) {
    return true
  }

  const contain = computedStyle.contain
  if (
    contain &&
    contain !== 'none' &&
    contain.split(/\s+/).some((value) => CONTAIN_CONTAINING_BLOCK_VALUES.has(value))
  ) {
    return true
  }

  const contentVisibility = computedStyle.contentVisibility
  if (contentVisibility && contentVisibility !== 'visible') {
    return true
  }

  return false
}

function hasContainingBlockAncestor(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (establishesContainingBlock(getComputedStyle(node))) {
      return true
    }
  }
  return false
}

function isOrHasContainingBlockAncestor(el) {
  return establishesContainingBlock(getComputedStyle(el)) || hasContainingBlockAncestor(el)
}

function parsePx(value) {
  const n = parseFloat(value)
  return Number.isNaN(n) ? 0 : n
}

// WCAG 1.4.10 (Reflow): uiv centers a popup horizontally on its trigger with
// no viewport clamping, so a trigger near either edge of a narrow (320px)
// column produces a `left` that pushes the fixed-positioned popup off-screen
// — which forces the page to allow horizontal scroll. Keep the popup's own
// left/right edges within the viewport, falling back to the margin alone if
// the popup itself is wider than the viewport.
const VIEWPORT_EDGE_MARGIN = 8

// Clamping `left` alone isn't enough when the popup itself is wider than the
// viewport (e.g. a resource preview thumbnail at 320px): `left` would bottom
// out at VIEWPORT_EDGE_MARGIN but the popup's right edge still overflows.
// Cap the box with `max-width` first and re-measure before computing `left`.
function clampToViewport(popup, left, popupWidth) {
  const availableWidth = Math.max(0, window.innerWidth - VIEWPORT_EDGE_MARGIN * 2)
  let width = popupWidth
  if (width > availableWidth) {
    popup.style.maxWidth = `${availableWidth}px`
    width = popup.offsetWidth
  }
  const maxLeft = Math.max(VIEWPORT_EDGE_MARGIN, window.innerWidth - width - VIEWPORT_EDGE_MARGIN)
  return Math.min(Math.max(left, VIEWPORT_EDGE_MARGIN), maxLeft)
}

// The horizontal clamp above has no vertical counterpart, so a popup taller
// than the viewport (e.g. a resource preview at a short, heavily-zoomed
// viewport) stays `position: fixed` with an unbounded `top`/height — it can
// render mostly or entirely off-screen with no way to scroll to the rest,
// including its own primary action button. Cap the box height and let it
// scroll internally first, then clamp `top` the same way `left` is clamped.
function clampVerticalToViewport(popup, top, popupHeight) {
  const availableHeight = Math.max(0, window.innerHeight - VIEWPORT_EDGE_MARGIN * 2)
  let height = popupHeight
  if (height > availableHeight) {
    popup.style.maxHeight = `${availableHeight}px`
    popup.style.overflowY = 'auto'
    height = popup.offsetHeight
  }
  const maxTop = Math.max(VIEWPORT_EDGE_MARGIN, window.innerHeight - height - VIEWPORT_EDGE_MARGIN)
  return Math.min(Math.max(top, VIEWPORT_EDGE_MARGIN), maxTop)
}

// Moves `popup` to be `trigger`'s next sibling (or, when `hostEl` is given,
// the last child of `hostEl`) and repositions it as a viewport-fixed
// element, preserving where uiv already placed it. Returns a `release()` to
// call on hide/destroy, or `null` if relocation was declined (popup is left
// exactly where uiv put it).
//
// `hostEl` exists for a trigger whose own parent enforces ARIA owned-element
// rules a relocated popup would violate (e.g. a `role="listbox"` option) —
// it lets the caller pick a DOM parent outside that subtree. Positioning is
// still derived from `trigger`'s own `getBoundingClientRect()`, so visually
// the popup lands in the same place regardless of which parent it's hosted
// under. An external host is held to the same containing-block rule as the
// trigger's ancestors.
export function placePopupBesideTrigger(popup, trigger, hostEl) {
  if (!popup || !trigger || !trigger.parentNode) return null
  if (hostEl === undefined) hostEl = trigger.parentNode
  if (!hostEl) return null
  if (hasContainingBlockAncestor(trigger)) return null
  if (hostEl !== trigger.parentNode && isOrHasContainingBlockAncestor(hostEl)) return null

  // uiv's resetPosition() already set these as document-absolute offsets
  // before emitting 'show'; capture them before moving anything.
  const documentTop = parsePx(popup.style.top)
  const documentLeft = parsePx(popup.style.left)

  // Track the trigger's own rect delta rather than window.scrollX/Y alone —
  // a nested `overflow` ancestor can scroll (moving the trigger) without the
  // window scrolling at all, and getBoundingClientRect() reflects both.
  const initialTriggerRect = trigger.getBoundingClientRect()
  const initialViewportTop = documentTop - window.scrollY
  const initialViewportLeft = documentLeft - window.scrollX

  if (hostEl === trigger.parentNode) {
    hostEl.insertBefore(popup, trigger.nextSibling)
  } else {
    hostEl.appendChild(popup)
  }
  popup.style.position = 'fixed'
  // clampToViewport()/clampVerticalToViewport() write inline max-width/
  // max-height/overflow-y when the popup is too big; reset to these before
  // every measurement so a later reposition (viewport growing back out) can
  // grow past a stale cap from an earlier, smaller one.
  const originalMaxWidth = popup.style.maxWidth
  const originalMaxHeight = popup.style.maxHeight
  const originalOverflowY = popup.style.overflowY

  const reposition = () => {
    popup.style.maxWidth = originalMaxWidth
    popup.style.maxHeight = originalMaxHeight
    popup.style.overflowY = originalOverflowY
    const triggerRect = trigger.getBoundingClientRect()
    const left = initialViewportLeft + triggerRect.left - initialTriggerRect.left
    const top = initialViewportTop + triggerRect.top - initialTriggerRect.top
    popup.style.left = `${clampToViewport(popup, left, popup.offsetWidth)}px`
    popup.style.top = `${clampVerticalToViewport(popup, top, popup.offsetHeight)}px`
  }
  reposition()

  // Capture phase: a nested scroll container's scroll doesn't bubble.
  window.addEventListener('scroll', reposition, true)
  // A viewport resize (narrowing, orientation change) can leave `left`
  // overflowing even when the popup's own box doesn't change size, which the
  // ResizeObserver below can't see.
  window.addEventListener('resize', reposition)

  // WCAG 1.4.10: the clamp above only sees the popup's width at show time —
  // a popup whose content includes an <img> with no width/height attribute
  // (e.g. ResourcePopoverPreview's thumbnail) measures near its text-only
  // width until the image loads, then grows past the already-clamped
  // `left`, reopening the same off-screen overflow. Re-run the clamp
  // whenever the popup's own box actually resizes, not only on scroll.
  // Unavailable in some test environments (jsdom) — relocation still works
  // without it, just without the late-growth re-clamp.
  let resizeObserver
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(reposition)
    resizeObserver.observe(popup)
  }

  return () => {
    window.removeEventListener('scroll', reposition, true)
    window.removeEventListener('resize', reposition)
    if (resizeObserver) resizeObserver.disconnect()
    popup.style.maxWidth = originalMaxWidth
    popup.style.maxHeight = originalMaxHeight
    popup.style.overflowY = originalOverflowY
  }
}
